// ISDP CUSTOM: Global toast (snackbar) system. A provider mounted at app root
// exposes useToast() → { success, error, info }. Toasts stack bottom-right, are
// ODX-styled + dark-mode aware, and auto-dismiss. Replaces ad-hoc error banners
// and gives positive feedback on actions (the old Hub's "beautiful toasts").
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import {
  ToastContext,
  useToast,
  type ToastApi,
} from '@internal/plugin-isdp-common/ui';
import { feedbackMessages } from '../../i18n/feedback';
import './ToastProvider.css';

// Re-exported so existing in-app imports (`../feedback/ToastProvider`) keep working;
// the context + hook now live in isdp-common so the admin plugin shares them.
export { useToast };

type ToastKind = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

const AUTO_DISMISS_MS = 4200;
const ICON: Record<ToastKind, string> = {
  success: 'core::check',
  error: 'core::error-filled',
  info: 'core::info',
};

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslationRef(feedbackMessages);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seq = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts(list => list.filter(item => item.id !== id));
  }, []);

  const push = useCallback(
    (kind: ToastKind, message: string) => {
      seq.current += 1;
      const id = seq.current;
      setToasts(list => [...list, { id, kind, message }]);
      setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: m => push('success', m),
      error: m => push('error', m),
      info: m => push('info', m),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="devhub-toast-stack" role="region" aria-label={t('notifications')}>
          {toasts.map(toast => (
            <div key={toast.id} className={`devhub-toast devhub-toast--${toast.kind}`} role="status">
              <odx-icon className="devhub-toast__icon" name={ICON[toast.kind]} size="sm" />
              <span className="devhub-toast__msg">{toast.message}</span>
              <button
                type="button"
                className="devhub-toast__close"
                aria-label={t('dismiss')}
                onClick={() => dismiss(toast.id)}
              >
                <odx-icon name="core::close" size="sm" />
              </button>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
};
